import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SignOut, User, Crown, List, X } from "@phosphor-icons/react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    // Close the mobile sheet whenever the route changes.
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname, location.hash]);

    // Lock body scroll while the mobile sheet is open.
    useEffect(() => {
        if (menuOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
        return undefined;
    }, [menuOpen]);

    const onLogout = () => {
        logout();
        setMenuOpen(false);
        navigate("/");
    };

    const linkClass = ({ isActive }) =>
        `text-sm font-medium uppercase tracking-wider transition-snappy px-1 ${
            isActive ? "text-primary" : "text-zinc-400 hover:text-primary"
        }`;

    const mobileLinkClass = ({ isActive }) =>
        `block py-3 border-b border-border font-mono uppercase tracking-widest text-base ${
            isActive ? "text-primary" : "text-zinc-200"
        }`;

    return (
        <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
            <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
                    <span className="font-black text-xl tracking-tighter neon-text text-primary">KICK</span>
                    <span className="font-black text-xl tracking-tighter text-white">LOOM</span>
                </Link>

                {/* DESKTOP NAV */}
                <nav className="hidden md:flex items-center gap-8">
                    {user ? (
                        <>
                            <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">Dashboard</NavLink>
                            <NavLink to="/live" className={linkClass} data-testid="nav-live">Live</NavLink>
                            <NavLink to="/results" className={linkClass} data-testid="nav-results">Results</NavLink>
                            <NavLink to="/fantasy" className={linkClass} data-testid="nav-fantasy">Fantasy AI</NavLink>
                            {user.userLevel !== "vip" && (
                                <NavLink to="/subscription" className={linkClass} data-testid="nav-subscription">Upgrade</NavLink>
                            )}
                            <NavLink to="/settings" className={linkClass} data-testid="nav-settings">Settings</NavLink>
                            {user.isAdmin && (
                                <NavLink to="/admin" className={linkClass} data-testid="nav-admin">Admin</NavLink>
                            )}
                        </>
                    ) : (
                        <>
                            <Link to="/#features" className={linkClass({ isActive: false })}>Features</Link>
                            <NavLink to="/live" className={linkClass} data-testid="nav-live-public">Live</NavLink>
                            <NavLink to="/results" className={linkClass} data-testid="nav-results-public">Results</NavLink>
                            <Link to="/#plans" className={linkClass({ isActive: false })}>Plans</Link>
                        </>
                    )}
                </nav>

                <div className="flex items-center gap-2 md:gap-3">
                    {/* DESKTOP AUTH ACTIONS */}
                    {user ? (
                        <div className="hidden md:flex items-center gap-3">
                            <div className="hidden sm:flex items-center gap-2 px-3 py-1 border border-border bg-card">
                                {user.userLevel === "vip" ? (
                                    <Crown size={14} weight="fill" className="text-primary" />
                                ) : (
                                    <User size={14} weight="bold" className="text-zinc-400" />
                                )}
                                <span className="font-mono text-xs uppercase tracking-wider text-zinc-300">
                                    {user.userLevel}
                                </span>
                            </div>
                            <Button
                                variant="outline" size="sm" onClick={onLogout} data-testid="nav-logout-btn"
                                className="rounded-none border-border hover:border-primary hover:text-primary"
                            >
                                <SignOut size={14} weight="bold" />
                                <span className="ml-1.5 uppercase tracking-wider text-xs">Logout</span>
                            </Button>
                        </div>
                    ) : (
                        <div className="hidden md:flex items-center gap-3">
                            <Link to="/login" data-testid="nav-login-btn">
                                <Button variant="ghost" size="sm" className="rounded-none uppercase tracking-wider text-xs">
                                    Login
                                </Button>
                            </Link>
                            <Link to="/register" data-testid="nav-register-btn">
                                <Button size="sm" className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider text-xs">
                                    Get Started
                                </Button>
                            </Link>
                        </div>
                    )}

                    {/* HAMBURGER — mobile only */}
                    <button
                        type="button"
                        onClick={() => setMenuOpen((o) => !o)}
                        className="md:hidden p-2 -mr-2 text-zinc-200 hover:text-primary transition-snappy"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={menuOpen}
                        data-testid="nav-mobile-toggle"
                    >
                        {menuOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
                    </button>
                </div>
            </div>

            {/* MOBILE SHEET */}
            {menuOpen && typeof document !== "undefined" && createPortal(
                <div
                    className="md:hidden fixed inset-0 z-[60] bg-background/98 backdrop-blur-xl overflow-y-auto pt-16"
                    data-testid="nav-mobile-sheet"
                >
                    <div className="px-6 py-6">
                        {user && (
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                                <div className="flex items-center gap-2">
                                    {user.userLevel === "vip" ? (
                                        <Crown size={18} weight="fill" className="text-primary" />
                                    ) : (
                                        <User size={18} weight="bold" className="text-zinc-400" />
                                    )}
                                    <div>
                                        <div className="font-bold text-sm tracking-tight">{user.name || user.email}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                            {user.userLevel} tier
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <nav className="space-y-0" aria-label="Mobile navigation">
                            {user ? (
                                <>
                                    <NavLink to="/dashboard" className={mobileLinkClass} data-testid="nav-m-dashboard">Dashboard</NavLink>
                                    <NavLink to="/live" className={mobileLinkClass} data-testid="nav-m-live">Live Scores</NavLink>
                                    <NavLink to="/results" className={mobileLinkClass} data-testid="nav-m-results">Results</NavLink>
                                    <NavLink to="/fantasy" className={mobileLinkClass} data-testid="nav-m-fantasy">Fantasy AI</NavLink>
                                    {user.userLevel !== "vip" && (
                                        <NavLink to="/subscription" className={mobileLinkClass} data-testid="nav-m-upgrade">
                                            <span className="text-primary">★</span> Upgrade to VIP
                                        </NavLink>
                                    )}
                                    <NavLink to="/settings" className={mobileLinkClass} data-testid="nav-m-settings">Settings</NavLink>
                                    {user.isAdmin && (
                                        <NavLink to="/admin" className={mobileLinkClass} data-testid="nav-m-admin">Admin</NavLink>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Link to="/#features" onClick={() => setMenuOpen(false)} className="block py-3 border-b border-border font-mono uppercase tracking-widest text-base text-zinc-200">Features</Link>
                                    <NavLink to="/live" className={mobileLinkClass} data-testid="nav-m-live-public">Live Scores</NavLink>
                                    <NavLink to="/results" className={mobileLinkClass} data-testid="nav-m-results-public">Results</NavLink>
                                    <Link to="/#plans" onClick={() => setMenuOpen(false)} className="block py-3 border-b border-border font-mono uppercase tracking-widest text-base text-zinc-200">Plans</Link>
                                </>
                            )}
                        </nav>

                        <div className="mt-8 space-y-3">
                            {user ? (
                                <Button
                                    onClick={onLogout}
                                    data-testid="nav-m-logout-btn"
                                    className="w-full rounded-none bg-zinc-800 hover:bg-zinc-700 text-white font-bold uppercase tracking-wider h-11"
                                >
                                    <SignOut size={16} weight="bold" className="mr-2" /> Logout
                                </Button>
                            ) : (
                                <>
                                    <Link to="/login" data-testid="nav-m-login-btn" onClick={() => setMenuOpen(false)}>
                                        <Button variant="outline" className="w-full rounded-none border-border hover:border-primary hover:text-primary font-bold uppercase tracking-wider h-11">
                                            Login
                                        </Button>
                                    </Link>
                                    <Link to="/register" data-testid="nav-m-register-btn" onClick={() => setMenuOpen(false)}>
                                        <Button className="w-full rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider h-11">
                                            Get Started Free
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </header>
    );
}
